import { ClientPaginatedTable } from '@avanta/shared/components/ClientPaginatedTable';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { t } from '@avanta/shared/i18n';
import { type ColumnDef } from '@tanstack/react-table';
import { type Appointment } from 'types/appointments';
import { CONTACT_METHOD_KEY, getCenterName, getStatusBadge } from 'utils/appointments';
import { formatUnifiedForDisplay } from 'utils/date';
import { FutureAppointmentsActions } from '../FutureAppointmentsActions';
import { AppointmentsSectionMode, type AppointmentsSectionProps } from './types';

const getTableColumns = (
  mode: (typeof AppointmentsSectionMode)[keyof typeof AppointmentsSectionMode]
): ColumnDef<Appointment>[] => {
  const baseColumns: ColumnDef<Appointment>[] = [
    {
      id: 'date',
      accessorKey: 'dateTime',
      header: t('AppointmentsTable.date.label', { ns: LOCIZE_NS.APPOINTMENTS }),
      cell: ({ row }) => formatUnifiedForDisplay(row.original.dateTime),
      accessorFn: row => formatUnifiedForDisplay(row.dateTime),
    },
    {
      id: 'type',
      accessorKey: 'bookedContactMethod',
      header: t('AppointmentsTable.type.label', { ns: LOCIZE_NS.APPOINTMENTS }),
      cell: ({ row }) =>
        row.original.bookedContactMethod
          ? t(
              `AppointmentCard.types.${CONTACT_METHOD_KEY[row.original.bookedContactMethod] ?? row.original.bookedContactMethod}`,
              { ns: LOCIZE_NS.APPOINTMENTS }
            )
          : '',
      accessorFn: row =>
        row.bookedContactMethod
          ? t(
              `AppointmentCard.types.${CONTACT_METHOD_KEY[row.bookedContactMethod] ?? row.bookedContactMethod}`,
              { ns: LOCIZE_NS.APPOINTMENTS }
            )
          : '',
    },
    {
      id: 'center',
      accessorKey: 'center',
      header: t('AppointmentsTable.center.label', { ns: LOCIZE_NS.APPOINTMENTS }),
      cell: ({ row }) => getCenterName(row.original.bookedContactMethod, row.original.center),
      accessorFn: row => getCenterName(row.bookedContactMethod, row.center),
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: t('AppointmentsTable.status.label', { ns: LOCIZE_NS.APPOINTMENTS }),
      cell: ({ row }) => getStatusBadge(row.original),
    },
  ];

  if (mode === AppointmentsSectionMode.future) {
    baseColumns.push({
      id: 'actions',
      header: t('AppointmentsTable.actions.label', { ns: LOCIZE_NS.APPOINTMENTS }),
      cell: ({ row }) => <FutureAppointmentsActions appointment={row.original} />,
    });
  }

  return baseColumns;
};

export const AppointmentsSection = ({
  appointments,
  isLoading,
  title,
  emptyMessage,
  mode = AppointmentsSectionMode.past,
}: AppointmentsSectionProps): React.ReactElement => {
  const columns = getTableColumns(mode);

  return (
    <ClientPaginatedTable
      data={appointments}
      columns={columns}
      isLoading={isLoading}
      emptyMessage={emptyMessage}
      title={title}
      withSearch
    />
  );
};
